import { computed, onBeforeUnmount, onMounted, ref, toValue, unref, watch } from 'vue';

import { useEventHub } from '@satellite/composables/useEventHub';
import yardSpotAreaService from '@satellite/services/yardSpotArea-service';

import type { ComputedRef, Ref } from 'vue';
import type { Maybe, Nullable } from '@nova/core';

export interface YardArea {
  id?: Maybe<string>;
  name?: Nullable<string>;
  warehouseId?: Maybe<string>;
  orgId?: Maybe<string>;
}

export interface UseYardAreaPresenterOptions {
  areaId?: Ref<Maybe<string>>;
  /**
   * Initial area data (already fetched). Used only for initial state.
   * If live=true, this will be refetched and overridden.
   */
  area?: Ref<Maybe<YardArea>> | ComputedRef<Maybe<YardArea>> | Maybe<YardArea>;
  /**
   * If true, fetches immediately when component mounts or when areaId changes.
   * If false, defers fetching until component enters viewport (requires v-intersect).
   * Default: true
   */
  immediate?: boolean;
  /**
   * If true, the area will be refreshed automatically when the area is updated (via eventHub).
   */
  live?: boolean;
}

export function useYardAreaPresenter(options: UseYardAreaPresenterOptions = {}) {
  const { areaId, area, immediate = true, live = false } = options;
  const eventHub = useEventHub();

  const fetchedArea = ref<Nullable<YardArea>>(null);
  const isLoading = ref(false);
  const needsFetch = ref(false); // Track if fetch is needed but deferred (not in viewport yet)
  // When immediate=true, we don't need viewport tracking - always consider it "in viewport"
  // When immediate=false, start as false and let v-intersect update it
  const isInViewport = ref(immediate);

  const effectiveAreaId = computed((): Maybe<string> => {
    const explicitAreaId = unref(areaId);

    if (explicitAreaId) {
      /**
       * Warn if both are provided but don't match.
       */
      const initialAreaId = toValue(area)?.id;

      if (initialAreaId && initialAreaId !== explicitAreaId) {
        console.warn(
          '[useYardAreaPresenter] areaId mismatch:',
          `explicit areaId (${explicitAreaId}) does not match area.id (${initialAreaId}). Using explicit areaId.`
        );
      }

      return explicitAreaId;
    }

    return toValue(area)?.id || null;
  });

  const currentArea = computed(() => {
    return {
      ...toValue(area),
      ...fetchedArea.value
    };
  });

  const areaName = computed((): Nullable<string> => {
    if (!currentArea.value) {
      return null;
    }

    return currentArea.value.name || null;
  });

  const fetchArea = async (id: string): Promise<void> => {
    if (!id) {
      fetchedArea.value = null;
      return;
    }

    isLoading.value = true;
    needsFetch.value = false; // Clear the flag when we start fetching

    try {
      const response = await yardSpotAreaService.getOneById(id);
      fetchedArea.value = response;
    } catch (err) {
      console.error('[useYardAreaPresenter] Failed to fetch area:', err);

      const error = err instanceof Error ? err : new Error(String(err));

      fetchedArea.value = null;

      // throw error for callers that await
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Public refresh method that checks if we need to fetch (based on what component needs to render)
   * and respects viewport visibility.
   * If component is not in viewport, defers the fetch until it is.
   */
  const refresh = async (onError?: (error: Error) => void): Promise<void> => {
    const id = effectiveAreaId.value;
    if (!id) {
      fetchedArea.value = null;
      needsFetch.value = false;
      return;
    }

    // Only check viewport when immediate=false (used in components with v-intersect)
    // When immediate=true, fetch immediately regardless of viewport
    if (!immediate && !isInViewport.value) {
      needsFetch.value = true;
      return;
    }

    try {
      needsFetch.value = false;
      await fetchArea(id);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      console.error('[useYardAreaPresenter] Failed to refresh area:', error);

      if (onError) {
        onError(error);
      }

      // throw error for callers that await
      throw error;
    }
  };

  /**
   * Called when component enters viewport.
   * Checks if there's a pending fetch and executes it.
   * Also checks if we need to refresh data (e.g., when live=true and data might be stale).
   * Handles both v-intersect object format and simple callback.
   */
  const handleIntersect = async (entry?: { isIntersecting?: boolean } | boolean) => {
    const isIntersecting = typeof entry === 'boolean' ? entry : entry?.isIntersecting ?? true;

    isInViewport.value = isIntersecting;

    // If component is in viewport and we have a pending fetch or live=true, refresh to ensure fresh data
    // This handles cases where events arrived while component was not visible
    if (isIntersecting && (needsFetch.value || live)) {
      // Fire-and-forget for automatic initial fetch
      try {
        await refresh();
      } catch {
        // Error already logged in refresh()
      }
    }
  };

  const handleUpdateSpotAreaEvent = async (event: { id: Nullable<string> }) => {
    if (event?.id === effectiveAreaId.value) {
      // Fire-and-forget for automatic initial fetch
      try {
        await refresh();
      } catch {
        // Error already logged in refresh()
      }
    }
  };

  /**
   * Watch explicit areaId only (not extracted from area)
   */
  watch(
    () => unref(areaId),
    async newId => {
      if (newId) {
        fetchedArea.value = null;

        // Fire-and-forget for automatic initial fetch
        try {
          await refresh();
        } catch {
          // Error already logged in refresh()
        }
      } else {
        fetchedArea.value = null;
        needsFetch.value = false;
      }
    }
  );

  onMounted(async () => {
    const hasName = Boolean(areaName.value);

    /**
     * If immediate or no area name, try to refresh
     * Refresh will check viewport and defer if needed
     */
    if (immediate || !hasName) {
      // Fire-and-forget for automatic initial fetch
      try {
        await refresh();
      } catch {
        // Error already logged in refresh()
      }
    }

    if (live) {
      eventHub.on('update-SpotArea', handleUpdateSpotAreaEvent);
    }
  });

  onBeforeUnmount(() => {
    if (live) {
      eventHub.off('update-SpotArea', handleUpdateSpotAreaEvent);
    }
  });

  return {
    area: currentArea,
    areaName,
    isLoading,
    refresh,
    onIntersect: handleIntersect,
    needsFetch
  };
}
